import React, { useMemo, useState } from "react";

import { ChevronDown, ChevronUp } from "lucide-react";

import { useHealthScoreThresholds } from "../../../../../Hooks/useHealthScoreThresholds";
import { normalizeBusinessHealthThresholds } from "../../../../../Utils/HealthThresholds";
import { HEALTH_DOT_COLORS } from "../../../../../Utils/Helper/businessHealth";

const LEGEND_CELL_PAD = "flex flex-col items-start gap-2.5 min-w-0 px-3 py-3";
const LEGEND_CHIP =
  "inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border border-gray-200 bg-white px-2.5 py-1 text-sm font-medium text-gray-700";

/** Color / fill chips from the Always On briefing legend (Reference, Business Health, Account Fit). */
export const AccountBriefingLegendContent: React.FC<{ compact?: boolean }> = ({
  compact = false,
}) => {
  const { businessHealth: bhThresholds } = useHealthScoreThresholds();

  const bhBands = useMemo(() => {
    const bh = normalizeBusinessHealthThresholds(bhThresholds);
    const concernHi = Math.max(bh.concern_min, bh.stable_min - 1);
    const stableHi = Math.max(bh.stable_min, bh.strong_above - 1);
    return {
      red: `Red: ${bh.concern_min}–${concernHi}`,
      yellow: `Yellow: ${bh.stable_min}–${stableHi}`,
      green: `Green: ${bh.strong_above}–100`,
    };
  }, [bhThresholds]);

  const cellPad = compact
    ? "flex flex-col items-start gap-1.5 px-1 py-0.5"
    : LEGEND_CELL_PAD;
  const chipRow = compact
    ? "flex flex-row flex-nowrap items-center gap-2 text-sm text-gray-800"
    : "flex flex-row flex-wrap items-center gap-2 text-sm text-gray-800";

  return (
    <div
      className={
        compact
          ? "flex w-max min-w-[32rem] flex-col items-stretch gap-2.5"
          : "grid grid-cols-1 md:grid-cols-3 md:gap-0"
      }
      role="region"
      aria-label="Legend for Account Intelligence Briefings"
    >
      <div className={cellPad}>
        <h4 className="font-medium text-gray-900 text-sm m-0 leading-tight">
          Reference
        </h4>
        <div className={chipRow}>
          <div className={LEGEND_CHIP}>
            <div
              className="w-5 h-5 rounded-full bg-[hsl(0deg_0%_25.2%/20%)] flex-shrink-0"
              aria-hidden
            />
            <span className="leading-snug">Gray = 100%</span>
          </div>
          <div className={LEGEND_CHIP}>
            <span
              className="relative inline-flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full 
                  bg-[hsl(0deg_0%_25.2%/20%)]"
              aria-hidden
            >
              <span className="h-1 w-1 rounded-full bg-slate-400" />
            </span>
            <span className="leading-snug">KPIs Data not available</span>
          </div>
        </div>
      </div>

      <div className={cellPad}>
        <h4 className="font-medium text-gray-900 text-sm m-0 leading-tight">
          Business Health
        </h4>
        <div className={chipRow}>
          <span className={LEGEND_CHIP}>
            <span
              className="inline-block w-2.5 h-2.5 rounded-full flex-shrink-0"
              style={{ backgroundColor: HEALTH_DOT_COLORS.red }}
              aria-hidden
            />
            {bhBands.red}
          </span>
          <span className={LEGEND_CHIP}>
            <span
              className="inline-block w-2.5 h-2.5 rounded-full flex-shrink-0"
              style={{ backgroundColor: HEALTH_DOT_COLORS.yellow }}
              aria-hidden
            />
            {bhBands.yellow}
          </span>
          <span className={LEGEND_CHIP}>
            <span
              className="inline-block w-2.5 h-2.5 rounded-full flex-shrink-0"
              style={{ backgroundColor: HEALTH_DOT_COLORS.green }}
              aria-hidden
            />
            {bhBands.green}
          </span>
        </div>
      </div>

      <div className={cellPad}>
        <h4 className="font-medium text-gray-900 text-sm m-0 leading-tight">
          Account Fit
        </h4>
        <div className={chipRow}>
          <span className={LEGEND_CHIP}>
            <span
              className="relative inline-flex items-center justify-center w-5 h-5 rounded-full border border-gray-300 bg-gray-100 flex-shrink-0"
              aria-hidden
            >
              <span className="w-1 h-1 bg-green-600 rounded-full" />
            </span>
            <span className="leading-snug">L (25%): Low</span>
          </span>
          <span className={LEGEND_CHIP}>
            <span
              className="relative inline-flex items-center justify-center w-5 h-5 rounded-full border border-gray-300 bg-gray-100 flex-shrink-0"
              aria-hidden
            >
              <span className="w-2.5 h-2.5 bg-orange-500 rounded-full" />
            </span>
            <span className="leading-snug">M (50%): Medium</span>
          </span>
          <span className={LEGEND_CHIP}>
            <span
              className="w-5 h-5 bg-green-500 rounded-full flex-shrink-0"
              aria-hidden
            />
            <span className="leading-snug">H (100%): High</span>
          </span>
        </div>
      </div>
    </div>
  );
};

/**
 * Inline legend for Account Intelligence briefing visualizations (100% gray
 * reference, KPIs Data not available, business health colors, account fit).
 * Business Health ranges come from vendor Settings thresholds.
 */
const AccountBriefingLegend: React.FC = () => {
  const [isExpanded, setIsExpanded] = useState(true);

  return (
    <div className="account-briefing-legend rounded-xl bg-white text-gray-700 shadow-sm overflow-hidden">
      <div className="flex items-center justify-between px-3 py-3 border-b border-gray-200 bg-white">
        <p className="mb-0 max-w-full leading-relaxed text-gray-700 text-sm">
          Real-time AI-powered account intelligence at your fingertips. Monitor
          customer business health, KPIs, engagement signals, and leading
          indicators across any account and executive sponsor portfolio.
        </p>
        <button
          type="button"
          onClick={() => setIsExpanded(prev => !prev)}
          className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-600"
          aria-expanded={isExpanded}
          aria-controls="account-briefing-legend-content"
        >
          {isExpanded ? (
            <ChevronDown className="size-4" aria-hidden />
          ) : (
            <ChevronUp className="size-4" aria-hidden />
          )}
        </button>
      </div>

      {isExpanded && (
        <div id="account-briefing-legend-content">
          <AccountBriefingLegendContent />
        </div>
      )}
    </div>
  );
};

export default AccountBriefingLegend;
